/**
 * Why a training run failed, in enough detail to decide what to change.
 *
 * A failed run used to render as the single sentence `summary.error` holds, on
 * an otherwise empty pane. That sentence is the least useful part of the
 * failure: nearly every run dies with an empty frame, and "no rows" means
 * something different — and calls for a different fix — depending on whether the
 * historian returned nothing, the cleaning gates dropped everything, or the fit
 * itself threw. The job records the stage and the row counts behind it
 * (`summary.failure`), so this panel shows the funnel rather than the sentence.
 */

import {
  Badge,
  Callout,
  ErrorText,
  Hint,
  Panel,
  PanelTitle,
  Row,
  StatTable,
} from 'features/sentinelDesign/designStyles'
import type {
  SentinelRunFailure,
  SentinelRunFunnel,
} from 'features/sentinels/types'
import { FC } from 'react'

/** What each stage name means in terms of what the user would change. */
const STAGE_EXPLANATION: Record<string, string> = {
  pull: 'The historian returned nothing for this window, so no model could be fitted. Check the window and that these tag ids really carry data.',
  clean:
    'Rows were pulled, but the plan left none of them usable. This is almost always a cleaning gate: a gate drops the whole row for every tag whenever the gated tag is out of range or has no value in that bucket.',
  fit: 'The data survived cleaning and the fit itself failed. The message below is the fitter’s own.',
  refit:
    'The honest split fit worked; the full-window refit that produces the served model did not.',
}

const STAGE_LABEL: Record<string, string> = {
  pull: 'historian pull',
  clean: 'cleaning and gating',
  fit: 'model fit',
  refit: 'full-window refit',
}

/** Row counts in pipeline order; a stage the job never reached is left out. */
const funnelRows = (
  funnel: SentinelRunFunnel
): [string, number | undefined][] => [
  ['Rows pulled', funnel.raw_grid_rows],
  ['After cleaning', funnel.after_cleaning_rows],
  ['After gates', funnel.after_gate_rows],
  ['After aligning drivers', funnel.aligned_grid_rows],
  ['With an observed target', funnel.target_observed_rows],
]

const bucketLabel = (funnel: SentinelRunFunnel): string | undefined => {
  if (funnel.sampling_mode === 'raw') return 'raw historian timestamps'
  if (!funnel.interval) return undefined
  const unit = (funnel.interval_unit ?? 'minutes').replace(/s$/, '')
  return `${funnel.interval}-${unit} buckets`
}

const FunnelTable: FC<{ funnel: SentinelRunFunnel }> = ({ funnel }) => {
  const rows = funnelRows(funnel).filter(
    ([, value]) => typeof value === 'number'
  )
  const perTag = Object.entries(funnel.raw_observed_per_tag ?? {})
  const emptyTags = perTag.filter(([, observed]) => !observed)
  const buckets = bucketLabel(funnel)
  if (rows.length === 0 && perTag.length === 0) return null

  return (
    <>
      {rows.length > 0 && (
        <StatTable>
          <tbody>
            {rows.map(([label, value]) => (
              <tr key={label}>
                <th scope='row'>{label}</th>
                <td>{(value as number).toLocaleString()}</td>
              </tr>
            ))}
            {buckets && (
              <tr>
                <th scope='row'>Resolution</th>
                <td>{buckets}</td>
              </tr>
            )}
          </tbody>
        </StatTable>
      )}
      {perTag.length > 0 && (
        <>
          <Hint>
            {emptyTags.length > 0
              ? `${emptyTags.length} of ${perTag.length} tags returned no values at all.`
              : `Every one of the ${perTag.length} tags returned values, so the rows were lost after the pull.`}
          </Hint>
          <StatTable>
            <thead>
              <tr>
                <th>Tag</th>
                <th>Values pulled</th>
              </tr>
            </thead>
            <tbody>
              {perTag.map(([tag, observed]) => (
                <tr key={tag}>
                  <td>{tag}</td>
                  <td>{observed ? observed.toLocaleString() : 'none'}</td>
                </tr>
              ))}
            </tbody>
          </StatTable>
        </>
      )}
    </>
  )
}

export interface RunFailurePanelProps {
  /** `summary.error`: always present on a failed run, even bare. */
  error?: string
  /** `summary.failure`: present when the job could name a stage. */
  failure?: SentinelRunFailure
  /** The last thing the job said it was doing, when it said anything. */
  lastStage?: string
  requestedAt?: string
}

export const RunFailurePanel: FC<RunFailurePanelProps> = ({
  error,
  failure,
  lastStage,
  requestedAt,
}) => {
  const stage = failure?.stage || undefined
  const phase = failure?.phase
  const message =
    failure?.error || error || 'The training job reported no reason.'
  const explanation = stage ? STAGE_EXPLANATION[stage] : undefined

  return (
    <Panel aria-label='Why this run failed'>
      <Row>
        <PanelTitle>Why this run failed</PanelTitle>
        <Badge $tone='error'>failed</Badge>
        {stage && <Badge $tone='error'>{STAGE_LABEL[stage] ?? stage}</Badge>}
        {phase === 'refit' && <Badge $tone='warning'>full-window refit</Badge>}
      </Row>
      {requestedAt && (
        <Hint>Started {new Date(requestedAt).toLocaleString()}.</Hint>
      )}

      <ErrorText>{message}</ErrorText>

      {explanation ? (
        <Callout $tone='error'>{explanation}</Callout>
      ) : (
        <Callout $tone='warning'>
          This run recorded no stage, so all there is to go on is the message
          above. Runs that fail this way usually died before the fit started —
          check the design context it was planned from.
          {lastStage ? ` The last stage it reported was “${lastStage}”.` : ''}
        </Callout>
      )}

      {failure?.funnel && <FunnelTable funnel={failure.funnel} />}
    </Panel>
  )
}
